'use client'

import { useRef, useState } from 'react'
import Link from 'next/link'
import { useLocale, useTranslations } from 'next-intl'
import { useSearchParams } from 'next/navigation'
import type { UseFormReturn } from 'react-hook-form'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Toast } from '@skop-app/lib-front'
import { DynamicForm } from '@skop-app/lib-front/dynamic-form'
import { validationRules } from '@skop-app/lib-domain/constants'

import { useRouter } from '@/navigation'
import { ACCOUNT_RECOVERY_ENABLED } from '@/services/accountLifecycle/accountLifecycle.shared'
import { getLoginFields } from '@/schemas/account.schema'
import { openModal } from '@/components/modals/modal.registry.events'
import { loginUser } from '@/services/authentification'
import {
  resolvePostAuthRedirectPath,
  storePostAuthRedirectPath,
} from '@/services/auth/clientAuthState'
import { AUTH_REDIRECT_QUERY_PARAM } from '@/services/auth/redirectPath'
import {
  clearPublicAuthLocalState,
  completeAuthNavigation,
} from '@/services/auth/postAuth.client'
import {
  isCognitoSessionRecoveryError,
  isUnexpectedSignInInterruptionError,
  isUserAlreadyAuthenticatedError,
} from '@/services/auth/cognitoErrors.client'
import {
  getNormalizedAuthErrorName,
  sanitizeAuthErrorMessage,
} from '@/services/auth/normalizeAuthError'
import { captureSessionEvent } from '@/services/auth/sessionTelemetry.client'
import { collectAmplifyAuthStorageDiagnostics } from '@/services/auth/amplifyStorageCleanup.client'
import {
  recoverUnavailableLoginSession,
  type UnavailableLoginSessionCleanupReason,
} from '@/services/auth/anonymousSession.client'
import {
  hasAuthenticatedUserSession,
  setPersistSessionPreference,
} from '@/services/auth/session.client'

type RunSignInAttemptParams = {
  emailCleaned: string
  password: string
  safeRedirectPath: string
  router: ReturnType<typeof useRouter>
  queryClient: ReturnType<typeof useQueryClient>
  toast: Pick<typeof Toast, 'classic' | 'error'>
  t: ReturnType<typeof useTranslations>
  onSessionRecoveryRequired: (
    reason: UnavailableLoginSessionCleanupReason,
  ) => void
}

function openSignupConfirmationFromLogin(
  email: string,
  redirectUrl: string,
): boolean {
  return openModal('confirmationCode', {
    email,
    name: '',
    redirectUrl,
  })
}

function openForgotPasswordFromLogin(
  initialEmail?: string,
  options?: {
    autoRequestResetCode?: boolean
  },
): boolean {
  return openModal('forgotPassword', {
    initialEmail,
    ...options,
  })
}

function handleKnownSignInStep(
  step: string | undefined,
  email: string,
  redirectUrl: string,
): boolean {
  switch (step) {
    case 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED':
      return openModal('newPasswordRequired', { redirectUrl })
    case 'CONFIRM_SIGN_UP':
      return openSignupConfirmationFromLogin(email, redirectUrl)
    case 'RESET_PASSWORD':
      return openForgotPasswordFromLogin(email, {
        autoRequestResetCode: true,
      })
    default:
      return false
  }
}

function handleKnownAuthErrorByName(
  normalizedErrorName: string | undefined,
  email: string,
  redirectUrl: string,
): boolean {
  switch (normalizedErrorName) {
    case 'UserNotConfirmedException':
      return openSignupConfirmationFromLogin(email, redirectUrl)
    case 'PasswordResetRequiredException':
      return openForgotPasswordFromLogin(email, {
        autoRequestResetCode: true,
      })
    default:
      return false
  }
}

function normalizeInitialEmail(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined

  const normalizedValue = value.toLowerCase().trim()
  return normalizedValue || undefined
}

async function runSignInAttempt(params: RunSignInAttemptParams): Promise<void> {
  const {
    emailCleaned,
    password,
    safeRedirectPath,
    router,
    queryClient,
    toast,
    t,
    onSessionRecoveryRequired,
  } = params
  const handlePublicAuthFailure = async (message: string) => {
    await clearPublicAuthLocalState(queryClient)
    toast.error(message)
  }

  const result = await loginUser({ username: emailCleaned, password })

  switch (result.status) {
    case 'success':
      if (result.data.isSignedIn) {
        const didNavigate = await completeAuthNavigation({
          router,
          safeRedirectPath,
          queryClient,
          toast,
          successMessage: t('Signin.success'),
        })
        if (didNavigate) {
          captureSessionEvent('login_success')
        } else {
          captureSessionEvent(
            'login_failure',
            'session_not_ready_after_signin',
            collectAmplifyAuthStorageDiagnostics(),
          )
          onSessionRecoveryRequired('session_not_ready_after_signin')
          await handlePublicAuthFailure(
            t('Signin.error-message-session-unavailable'),
          )
        }
        return
      }

      const step = result.data.nextStep?.signInStep
      if (handleKnownSignInStep(step, emailCleaned, safeRedirectPath)) {
        return
      }

      if (!step) {
        toast.error(t('Signin.error-message-temporary'))
        captureSessionEvent('login_failure', 'missing_signin_step')
        return
      }

      const message = validationRules.cognitoMessages[step]?.message
      if (message) {
        toast.error(message)
        captureSessionEvent('login_failure', `step_${step}`)
      } else {
        toast.error(t('Signin.error-message-temporary'))
        captureSessionEvent('login_failure', `unsupported_step_${step}`)
      }
      return

    case 'error': {
      if (isUserAlreadyAuthenticatedError(result.error)) {
        const hasSession = await hasAuthenticatedUserSession()
        if (hasSession) {
          const didNavigate = await completeAuthNavigation({
            router,
            safeRedirectPath,
            queryClient,
            toast,
            successMessage: t('Signin.success'),
          })
          if (didNavigate) {
            captureSessionEvent('login_success')
          } else {
            captureSessionEvent(
              'login_failure',
              'session_not_ready_after_already_authenticated',
              collectAmplifyAuthStorageDiagnostics(),
            )
            onSessionRecoveryRequired(
              'session_not_ready_after_already_authenticated',
            )
            await handlePublicAuthFailure(
              t('Signin.error-message-session-unavailable'),
            )
          }
        } else {
          captureSessionEvent(
            'login_failure',
            'already_authenticated_without_session',
          )
          await handlePublicAuthFailure(
            t('Signin.error-message-session-expired'),
          )
        }
        return
      }

      if (isCognitoSessionRecoveryError(result.error)) {
        const isUnexpectedInterruption = isUnexpectedSignInInterruptionError(
          result.error,
        )
        const reason = isUnexpectedInterruption
          ? 'unexpected_sign_in_interruption'
          : 'refresh_token_revoked'

        captureSessionEvent(
          'login_failure',
          reason,
          isUnexpectedInterruption
            ? collectAmplifyAuthStorageDiagnostics()
            : undefined,
        )
        if (isUnexpectedInterruption) {
          onSessionRecoveryRequired('unexpected_sign_in_interruption')
        }
        await handlePublicAuthFailure(
          isUnexpectedInterruption
            ? t('Signin.error-message-session-unavailable')
            : t('Signin.error-message-session-expired'),
        )
        return
      }

      const normalizedErrorName = getNormalizedAuthErrorName(result.error)
      if (
        handleKnownAuthErrorByName(
          normalizedErrorName,
          emailCleaned,
          safeRedirectPath,
        )
      ) {
        return
      }

      const errorMessage = normalizedErrorName
        ? validationRules.cognitoMessages[normalizedErrorName]?.message
        : undefined
      if (errorMessage) {
        toast.error(errorMessage)
      } else {
        toast.error(
          t('Signin.error-message', {
            error: sanitizeAuthErrorMessage(result.error),
          }),
        )
      }
      captureSessionEvent(
        'login_failure',
        normalizedErrorName ?? 'unknown_cognito_error',
      )
      return
    }
  }
}

export default function Login() {
  const locale = useLocale()
  const t = useTranslations()
  const form = useRef<UseFormReturn>(null)
  const submittingRef = useRef(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isRecovering, setIsRecovering] = useState(false)
  const [sessionRecoveryReason, setSessionRecoveryReason] =
    useState<UnavailableLoginSessionCleanupReason | null>(null)
  const [persistSession, setPersistSession] = useState(false)
  const router = useRouter()
  const searchParams = useSearchParams()
  const queryClient = useQueryClient()
  const redirectUrl = searchParams?.get(AUTH_REDIRECT_QUERY_PARAM)
  const safeRedirectPath = resolvePostAuthRedirectPath(redirectUrl)
  const authModeQuery =
    safeRedirectPath === '/'
      ? {}
      : { [AUTH_REDIRECT_QUERY_PARAM]: safeRedirectPath }

  const onSubmit = async ({
    email,
    password,
  }: {
    email: string
    password: string
  }) => {
    if (submittingRef.current) return
    submittingRef.current = true

    setIsLoading(true)
    setSessionRecoveryReason(null)
    captureSessionEvent('login_submit')
    const emailCleaned = email.toLowerCase().trim()

    storePostAuthRedirectPath(safeRedirectPath)
    setPersistSessionPreference(Boolean(persistSession))

    try {
      await runSignInAttempt({
        emailCleaned,
        password,
        safeRedirectPath,
        router,
        queryClient,
        toast: Toast,
        t,
        onSessionRecoveryRequired: (reason) => {
          form.current?.resetField('password')
          setSessionRecoveryReason(reason)
        },
      })
    } finally {
      submittingRef.current = false
      setIsLoading(false)
    }
  }

  const handleForgotPassword = () => {
    openForgotPasswordFromLogin(
      normalizeInitialEmail(form.current?.getValues('email')),
    )
  }

  const handleSessionRecovery = async () => {
    if (submittingRef.current || !sessionRecoveryReason) return
    submittingRef.current = true
    setIsRecovering(true)

    try {
      const authArtifactsCleared = await recoverUnavailableLoginSession(
        queryClient,
        sessionRecoveryReason,
      )
      setPersistSession(false)
      form.current?.resetField('password')

      if (!authArtifactsCleared) {
        Toast.error(t('Signin.session-recovery-failed'))
        return
      }

      setSessionRecoveryReason(null)
      Toast.classic(t('Signin.session-recovery-ready'))
      form.current?.setFocus('password')
    } finally {
      submittingRef.current = false
      setIsRecovering(false)
    }
  }

  const loginFields = getLoginFields({ t })

  return (
    <div className="lg:col-span-6">
      <div className="overflow-hidden rounded-lg border border-stone-200 bg-white shadow-sm sm:mx-auto sm:w-full sm:max-w-md">
        <nav
          aria-label={t('AuthLayout.navigation-label')}
          className="border-b border-stone-200 bg-white"
        >
          <div className="grid grid-cols-2">
            <Link
              href={{
                pathname: '/login',
                query: authModeQuery,
              }}
              aria-current="page"
              className="relative inline-flex min-h-12 items-center justify-center px-4 text-sm font-medium text-slate-700"
            >
              {t('AuthLayout.login-tab')}
              <span
                aria-hidden="true"
                className="absolute inset-x-6 bottom-0 h-0.5 rounded-full bg-slate-700"
              />
            </Link>
            <Link
              href={{
                pathname: '/signup',
                query: authModeQuery,
              }}
              className="relative inline-flex min-h-12 items-center justify-center px-4 text-sm font-medium text-slate-400 transition-colors hover:text-slate-700"
            >
              {t('AuthLayout.signup-tab')}
            </Link>
          </div>
        </nav>
        <div className="px-4 py-8 sm:px-10">
          <DynamicForm
            id="login-form"
            ref={form}
            fields={loginFields}
            onSubmit={onSubmit}
            locale={locale}
            values={{}}
          />
          <div className="mt-4 space-y-3">
            <button
              type="button"
              className="block w-full text-right text-sm font-medium text-orange-600 hover:text-orange-700 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2"
              onClick={handleForgotPassword}
            >
              {t('Signin.forgot-password')}
            </button>
            {ACCOUNT_RECOVERY_ENABLED ? (
              <a
                href={`/${locale}/inactive-account/recovery`}
                className="block text-right text-sm font-medium text-slate-700 hover:underline"
              >
                {t('AccountRecovery.link')}
              </a>
            ) : null}
            <label className="flex w-full cursor-pointer items-start gap-3 text-sm leading-5 text-slate-700">
              <input
                type="checkbox"
                name="persistSession"
                checked={persistSession}
                onChange={(event) => {
                  setPersistSession(event.target.checked)
                }}
                className="mt-0.5 h-4 w-4 rounded border-gray-300 text-orange-500 focus:ring-2 focus:ring-orange-500"
              />
              <span>{t('Signin.remember-me')}</span>
            </label>
            {sessionRecoveryReason ? (
              <div
                role="alert"
                className="rounded-md border border-orange-200 bg-orange-50 p-3"
              >
                <p className="text-sm leading-5 text-slate-700">
                  {t('Signin.session-recovery-description')}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  disabled={isLoading || isRecovering}
                  className="mt-3 w-full"
                  onClick={() => {
                    void handleSessionRecovery()
                  }}
                >
                  {t('Signin.retry')}
                </Button>
              </div>
            ) : null}
          </div>
          <Button
            form="login-form"
            type="submit"
            disabled={isLoading || isRecovering}
            className="mt-4 w-full"
          >
            {t('Signin.login')}
          </Button>
        </div>
        <div className="space-y-2 border-t border-stone-200 bg-stone-50 px-4 py-6 sm:px-10">
          <Link
            href={{
              pathname: '/signup',
              query: authModeQuery,
            }}
            className="group space-x-2 text-base leading-5 text-orange-600"
          >
            <span>{t('Signin.not-registered')}</span>
            <span className="text-slate-500 underline underline-offset-2 group-hover:text-slate-700">
              {t('Signin.create-account')}
            </span>
          </Link>
          <p className="text-xs leading-5 text-gray-500">
            {t('Signin.download-app')}{' '}
            <Link
              href="https://apple.co/32s6L8c"
              className="font-medium text-gray-900 hover:underline"
            >
              App Store
            </Link>{' '}
            {t('Signin.download-app-conjunction')}{' '}
            <Link
              href="https://skop.page.link/29hQ"
              className="font-medium text-gray-900 hover:underline"
            >
              Google Play
            </Link>
            .
          </p>
        </div>
      </div>
    </div>
  )
}
